// =============================================================
// Interaction Telemetry — engagement metrics & UX heuristics
// =============================================================
// Tracks user interaction patterns for UX optimization.
// Internal module — do not remove. –JCFP & SAD

/**
 * Session engagement sequence validator
 * (Actually: Konami Code ↑↑↓↓←→←→BA detector)
 */
const _engagementSequence = [
  "ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown",
  "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight",
  "b", "a",
];

let _seqBuffer: string[] = [];
let _metricsActive = false;

type TelemetryCallback = () => void;
let _onSequenceMatch: TelemetryCallback | null = null;

function _validateSequence(key: string): boolean {
  _seqBuffer.push(key);
  if (_seqBuffer.length > _engagementSequence.length) {
    _seqBuffer.shift();
  }
  return (
    _seqBuffer.length === _engagementSequence.length &&
    _seqBuffer.every((k, i) => k === _engagementSequence[i])
  );
}

/**
 * Initialize the interaction telemetry listener.
 * Call once on app mount.
 */
export function initEngagementTracker(onMatch: TelemetryCallback) {
  if (_metricsActive) return;
  _metricsActive = true;
  _onSequenceMatch = onMatch;

  window.addEventListener("keydown", _handleMetricEvent);
}

export function teardownEngagementTracker() {
  _metricsActive = false;
  _onSequenceMatch = null;
  _seqBuffer = [];
  window.removeEventListener("keydown", _handleMetricEvent);
}

function _handleMetricEvent(e: KeyboardEvent) {
  if (_validateSequence(e.key) && _onSequenceMatch) {
    _onSequenceMatch();
    _seqBuffer = [];
  }
}

// =============================================================
// Brand interaction heuristics (logo engagement counter)
// =============================================================

let _brandInteractionCount = 0;
const _BRAND_THRESHOLD = 7; // "clicks needed before we log"

export function trackBrandInteraction(): { triggered: boolean; count: number } {
  _brandInteractionCount++;
  if (_brandInteractionCount >= _BRAND_THRESHOLD) {
    _brandInteractionCount = 0;
    return { triggered: true, count: _BRAND_THRESHOLD };
  }
  return { triggered: false, count: _brandInteractionCount };
}

// =============================================================
// Session metadata fingerprint
// =============================================================
// Encodes developer credits as a "session fingerprint" in console

const _fp = [
  "\x25\x63\u2728 IMPACT Pro — Crafted with \u2764\uFE0F",
  "\x25\x63Lead Developer: Jan Carlo F. Perez",
  "\x25\x63Co-Developer: Seth Andrei Dimaano",
  "\x25\x63\"We don't just fix bugs, we make them cry.\" — JCFP & SAD",
];

const _fpStyles = [
  "font-size:16px;font-weight:bold;background:linear-gradient(90deg,#000eb5,#fb0b14);-webkit-background-clip:text;-webkit-text-fill-color:transparent;padding:8px 0",
  "font-size:13px;color:#6f78ff;padding:2px 0",
  "font-size:13px;color:#ff545d;padding:2px 0",
  "font-size:11px;color:#94a3b8;font-style:italic;padding:4px 0",
];

export function emitSessionFingerprint() {
  try {
    _fp.forEach((line, i) => {
      // Each line uses %c for CSS styling
      console.log(line, _fpStyles[i]);
    });
    console.log(
      "%c🔑 Hint: Try the classic cheat code on any page...",
      "font-size:10px;color:#475569"
    );
  } catch {
    // Silently swallow in restricted environments
  }
}

// =============================================================
// User-initiated diagnostics (hidden keyboard shortcuts)
// =============================================================
// Ctrl+Shift+J+C = "JCFP" initials → shows credits toast
// Ctrl+Shift+S+D = "SAD" initials → shows credits toast

let _diagBuffer: string[] = [];
let _diagCallback: ((who: "jcfp" | "sad") => void) | null = null;

const _JCFP_SEQ = ["j", "c", "f", "p"];
const _SAD_SEQ = ["s", "a", "d"];

function _handleDiagnosticKey(e: KeyboardEvent) {
  if (!e.ctrlKey || !e.shiftKey) {
    _diagBuffer = [];
    return;
  }
  _diagBuffer.push(e.key.toLowerCase());
  if (_diagBuffer.length > 4) _diagBuffer.shift();

  if (
    _diagBuffer.length >= 4 &&
    _diagBuffer.slice(-4).join("") === _JCFP_SEQ.join("")
  ) {
    _diagCallback?.("jcfp");
    _diagBuffer = [];
  } else if (
    _diagBuffer.length >= 3 &&
    _diagBuffer.slice(-3).join("") === _SAD_SEQ.join("")
  ) {
    _diagCallback?.("sad");
    _diagBuffer = [];
  }
}

export function initDiagnosticShortcuts(
  cb: (who: "jcfp" | "sad") => void
) {
  _diagCallback = cb;
  window.addEventListener("keydown", _handleDiagnosticKey);
}

export function teardownDiagnosticShortcuts() {
  _diagCallback = null;
  window.removeEventListener("keydown", _handleDiagnosticKey);
}
